import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { education } from "../../data/constanit";
import { GraduationCap, Calendar } from "lucide-react";

const Container = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 1;
  padding: 80px 24px 60px;

  @media (max-width: 768px) {
    padding: 60px 16px 40px;
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 1000px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Title = styled(motion.h2)`
  font-size: 42px;
  font-weight: 800;
  text-align: center;
  color: ${({ theme }) => theme.text_primary};
  margin-bottom: 12px;
  letter-spacing: -0.5px;

  span {
    background: linear-gradient(135deg, ${({ theme }) => theme.primary} 0%, ${({ theme }) => theme.secondary} 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  @media (max-width: 768px) {
    font-size: 32px;
  }
`;

const Description = styled(motion.p)`
  font-size: 16px;
  text-align: center;
  max-width: 620px;
  color: ${({ theme }) => theme.text_secondary};
  line-height: 1.7;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    font-size: 14.5px;
    margin-bottom: 24px;
  }
`;

const EducationGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
`;

const EducationCard = styled(motion.div)`
  background: ${({ theme }) => theme.card_light};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 20px;
  padding: 24px 28px;
  box-shadow: 0 14px 34px rgba(0, 0, 0, 0.25);
  display: flex;
  gap: 20px;
  align-items: flex-start;
  transition: all 0.25s ease;

  &:hover {
    border-color: ${({ theme }) => `${theme.primary}66`};
    box-shadow: 0 18px 40px rgba(0, 0, 0, 0.35);
    transform: translateY(-2px);
  }

  @media (max-width: 680px) {
    flex-direction: column;
    padding: 20px;
    gap: 14px;
  }
`;

const IconBox = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 14px;
  background: ${({ theme }) => `${theme.primary}18`};
  border: 1px solid ${({ theme }) => `${theme.primary}33`};
  color: ${({ theme }) => theme.primary};
  display: grid;
  place-items: center;
  flex-shrink: 0;
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const TopRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  flex-wrap: wrap;
  margin-bottom: 4px;
`;

const SchoolName = styled.h3`
  font-size: 19px;
  font-weight: 700;
  color: ${({ theme }) => theme.text_primary};
`;

const Degree = styled.h4`
  font-size: 14.5px;
  font-weight: 600;
  color: ${({ theme }) => theme.primary};
  margin-bottom: 8px;
`;

const DateBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 999px;
  background: ${({ theme }) => theme.bgSoft};
  border: 1px solid ${({ theme }) => theme.border};
  color: ${({ theme }) => theme.text_secondary};
`;

const Desc = styled.p`
  font-size: 14px;
  line-height: 1.65;
  color: ${({ theme }) => theme.text_secondary};
  margin-top: 4px;
`;

const Education = () => {
  return (
    <Container id="education">
      <ContentWrapper>
        <Title
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          Education <span>History</span>
        </Title>

        <Description
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
        >
          My university degrees and formal academic foundation in Computer Science.
        </Description>

        <EducationGrid>
          {education.map((item, idx) => (
            <EducationCard
              key={idx}
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.35, delay: idx * 0.1 }}
            >
              <IconBox>
                <GraduationCap size={22} />
              </IconBox>

              <CardContent>
                <TopRow>
                  <div>
                    <SchoolName>{item.school}</SchoolName>
                    <Degree>{item.degree}</Degree>
                  </div>
                  <DateBadge>
                    <Calendar size={12} />
                    {item.date}
                  </DateBadge>
                </TopRow>

                <Desc>{item.desc}</Desc>
              </CardContent>
            </EducationCard>
          ))}
        </EducationGrid>
      </ContentWrapper>
    </Container>
  );
};

export default Education;
